import { VerifiedGuard } from './core/authorization/verified-user.guard';
import { ErrorComponent } from './error/error.component';
// All application routing module should be here.
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AccountExpiredComponent } from './account-expired/account-expired.component';
import { ExpiredAccountGuard } from './core/authorization/expired-account.guard';
import { VerifyUserComponent } from './verify-user/verify-user.component';
import { TierAccountGuard } from './core/authorization/tier-account.guard';
import { AdminUserGuard } from './core/authorization/admin-user.guard';
import { BrandSettingsViewComponent } from './assets/brand-settings-view/brand-settings-view.component';
import { MediaLibraryViewComponent } from './shared/media-library-view/media-library-view.component';
import { redirectToSignUp } from './authentication/sign-up-access.guard';
import { Tier } from './shared/account/tier.model';

// Shared routes
const routes: Routes = [
  // Routes WITHOUT protection from expired account
  { path: 'account-expired', component: AccountExpiredComponent },
  {
    path: 'account',
    loadChildren: () => import('./authentication/authentication.module').then((m) => m.AuthenticationModule),
  },
  {
    path: 'signup/essential',
    redirectTo: redirectToSignUp(Tier.Essential),
    pathMatch: 'full',
  },
  {
    path: 'signup/pro',
    redirectTo: redirectToSignUp(Tier.Pro),
    pathMatch: 'full',
  },
  {
    path: 'error',
    component: ErrorComponent,
    data: { title: 'generic.error' },
  },
  {
    path: 'user',
    loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
  },
  {
    path: 'backoffice',
    loadChildren: () => import('./back-office/back-office.module').then((m) => m.BackOfficeModule),
  },
  { path: 'verify-user', component: VerifyUserComponent },
  {
    path: 'oauth',
    loadChildren: () => import('./oauth-callback/oauth-callback.module').then((m) => m.OAuthCallbackModule),
  },
  {
    path: 'oauth-consent',
    loadChildren: () => import('./oauth-consent/oauth-consent.module').then((m) => m.OAuthConsentModule),
  },
  {
    path: 'invitations',
    loadChildren: () => import('./invitations/invitations.module').then((m) => m.InvitationsModule),
  },
  // Routes WITH protection from expired account
  {
    path: '',
    canActivate: [VerifiedGuard],
    canActivateChild: [ExpiredAccountGuard],
    children: [
      // The dashboard's old address, mounted on the same module rather than redirected to '': an
      // empty-string redirect cancels the navigation to '/' and leaves the outlet blank.
      {
        path: 'new-primary-dashboard',
        loadChildren: () =>
          import('./new-primary-dashboard/new-primary-dashboard.module').then((m) => m.NewPrimaryDashboardModule),
        data: { title: 'new-primary-dashboard.title' },
      },
      {
        path: 'new-primary-route',
        loadChildren: () =>
          import('./new-primary-dashboard/new-primary-dashboard.module').then((m) => m.NewPrimaryDashboardModule),
        data: { title: 'new-primary-dashboard.title' },
      },
      {
        path: '',
        loadChildren: () =>
          import('./new-primary-dashboard/new-primary-dashboard.module').then((m) => m.NewPrimaryDashboardModule),
        data: { title: 'new-primary-dashboard.title' },
      },
      {
        path: 'insights/visits',
        canActivate: [TierAccountGuard],
        loadChildren: () =>
          import('./data-platform/pages/data-platform-visits-page.module').then((m) => m.DataPlatformVisitsPageModule),
        data: { access: 'visits' },
      },
      {
        path: 'insights/visits-old',
        canActivate: [TierAccountGuard],
        loadChildren: () => import('./visits/visits.module').then((m) => m.VisitsModule),
        data: { title: 'visits.title', access: 'visits' },
      },
      {
        path: 'response-lists',
        loadChildren: () =>
          import('./campaign/response-lists/response-lists-page.module').then((m) => m.ResponseListsPageModule),
        data: { title: 'response-lists.form-responses' },
      },
      {
        path: 'analytics',
        loadChildren: () => import('./analytics/analytics.module').then((m) => m.AnalyticsModule),
      },
      {
        path: 'onsite-experiences',
        loadChildren: () =>
          import('./onsite-experiences/onsite-experiences.module').then((m) => m.OnsiteExperiencesModule),
        data: { title: 'dashboard-new.title' },
      },
      {
        path: 'data-collection',
        loadChildren: () => import('./data-collection/data-collection.module').then((m) => m.DataCollectionModule),
        data: { title: 'data-collection.title' },
      },
      {
        path: 'app-experiences',
        loadChildren: () => import('./app-experiences/app-experiences.module').then((m) => m.AppExperiencesModule),
      },
      {
        path: 'insights/live',
        loadChildren: () => import('./audience/components/live/live.module').then((m) => m.LiveModule),
      },

      // Relative redirects: an absolute one drops the query params.
      { path: 'people', redirectTo: 'profiles', pathMatch: 'full' },
      {
        path: 'people/import',
        redirectTo: 'profiles',
        pathMatch: 'full',
      },
      {
        path: 'people/contact-fields',
        redirectTo: 'profiles/contact-fields',
        pathMatch: 'full',
      },
      {
        path: 'people/delete',
        redirectTo: 'profiles/delete',
        pathMatch: 'full',
      },
      {
        path: 'people/person',
        redirectTo: 'profiles',
        pathMatch: 'full',
      },
      { path: 'profiles/import', redirectTo: 'profiles', pathMatch: 'full' },

      {
        path: 'profiles',
        canActivate: [TierAccountGuard],
        data: { access: 'profiles' },
        loadChildren: () =>
          import('./data-platform/pages/data-platform-profiles-page.module').then(
            (m) => m.DataPlatformProfilesPageModule,
          ),
      },
      {
        path: 'profiles-old',
        canActivate: [TierAccountGuard],
        data: { access: 'profiles' },
        loadChildren: () => import('./people/people.module').then((m) => m.PeopleModule),
      },
      {
        path: 'consent',
        loadChildren: () => import('./consent/consent.module').then((m) => m.ConsentModule),
        data: { title: 'consent.title' },
      },
      {
        path: 'automations',
        canActivate: [TierAccountGuard],
        data: { access: 'automations' },
        loadChildren: () => import('./automations/automations.module').then((m) => m.AutomationsModule),
      },
      {
        path: 'automations-old',
        canActivate: [TierAccountGuard],
        data: { access: 'automations', legacyAutomations: true },
        loadChildren: () => import('./automations/automations.module').then((m) => m.AutomationsModule),
      },
      {
        path: 'settings',
        loadChildren: () => import('./account-settings/account-settings.module').then((m) => m.AccountSettingsModule),
      },
      {
        path: 'error',
        component: ErrorComponent,
        data: { title: 'generic.error' },
      },
      {
        path: 'account',
        loadChildren: () => import('./authentication/authentication.module').then((m) => m.AuthenticationModule),
      },
      {
        path: 'user',
        loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
      },
      {
        path: 'research',
        loadChildren: () => import('./research/research.module').then((m) => m.ResearchModule),
      },
      // Only the retired list lives here; the editor, settings and create flow stay on /campaigns.
      {
        path: 'campaign-old',
        loadChildren: () => import('./campaign/campaign.module').then((m) => m.CampaignModule),
      },
      // Order matters: this claims the bare url, deeper paths fall through to the mount below.
      { path: 'campaigns', redirectTo: 'onsite-experiences', pathMatch: 'full' },
      {
        path: 'campaigns',
        loadChildren: () => import('./campaign/campaign.module').then((m) => m.CampaignModule),
      },
      {
        path: 'audiences',
        loadChildren: () =>
          import('./data-platform/pages/data-platform-audiences-page.module').then(
            (m) => m.DataPlatformAudiencesPageModule,
          ),
      },
      {
        path: 'audiences-old',
        loadChildren: () => import('./audience/audience.module').then((m) => m.AudienceModule),
      },
      { path: 'coupons', redirectTo: 'assets/coupons', pathMatch: 'prefix' },
      // New /assets/* routes must also get a matching rewrite rule in staticwebapp.config.json, else deep-links/refresh 404 in prod (the physical /assets folder is excluded from navigationFallback).
      {
        path: 'assets/coupons',
        canActivate: [TierAccountGuard],
        data: { access: 'coupons' },
        loadChildren: () => import('./coupons/coupons.module').then((m) => m.CouponsModule),
      },
      {
        path: 'assets/brand-settings',
        component: BrandSettingsViewComponent,
        data: { title: 'settings.customization.brand-title' },
      },
      { path: 'data-platform/visits', redirectTo: 'insights/visits', pathMatch: 'full' },
      { path: 'data-platform/profiles', redirectTo: 'profiles', pathMatch: 'full' },
      { path: 'data-platform/audiences', redirectTo: 'audiences', pathMatch: 'full' },
      {
        path: 'data-platform',
        canActivate: [AdminUserGuard],
        loadChildren: () => import('./data-platform/data-platform-admin.module').then((m) => m.DataPlatformAdminModule),
        data: { title: 'data-platform.title' },
      },
      { path: 'data-sources', redirectTo: 'assets/datasources', pathMatch: 'prefix' },
      {
        path: 'assets/datasources',
        canActivate: [TierAccountGuard],
        data: { access: 'data-sources' },
        loadChildren: () => import('./data-sources/data-sources.module').then((m) => m.DataSourcesModule),
      },
      {
        path: 'data-exports',
        loadChildren: () => import('./data-exports/data-exports.module').then((m) => m.DataExportsModule),
      },
      {
        path: 'email-templates',
        loadChildren: () => import('./email-templates/email-templates.module').then((m) => m.EmailTemplatesModule),
      },
      {
        path: 'knowledge',
        loadChildren: () => import('./knowledge/knowledge.module').then((m) => m.KnowledgeModule),
        data: { title: 'knowledge.title' },
      },
      {
        path: 'assets/medialibrary',
        component: MediaLibraryViewComponent,
        data: { title: 'media-library-view.title' },
      },
    ],
  },
  { path: '**', redirectTo: '/' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
